import { useSnackbar } from 'notistack'
import { useContext, useEffect, useState } from 'react'
import { FieldErrors } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { generatePath, useNavigate } from 'react-router-dom'

import { fixAttributeDuplicatePaths } from 'features/ab-tests/api/utils/errors'
import { AbTestsAddEditContext } from 'features/ab-tests/contexts/AbTestsAddEditContext'
import { AbTestsAddSchema } from 'features/ab-tests/forms/AbTestsAddForm/abTestsAddSchema'
import { useFeatureFlag } from 'features/user/hooks/useFeatureFlag'
import { useWorkspaceParam } from 'features/workspace/hooks/useWorkspaceParam'
import { MutationInputError } from 'providers/tanstack-query/errors'
import { useMutation } from 'providers/tanstack-query/useMutation'
import { mapGraphQLErrors } from 'providers/tanstack-query/utils'
import { ROUTES } from 'routes'
import { toDTO } from './api/mapper'
import { UPDATE_AB_TEST } from './api/mutation'
import { Data, Variables } from './api/types'
import { UseUpdate } from './types'

export const useUpdateAbTest = (): UseUpdate => {
  const { workspaceId } = useWorkspaceParam()
  const navigate = useNavigate()
  const { t } = useTranslation('features/abTests', { keyPrefix: 'form.update' })
  const { enqueueSnackbar } = useSnackbar()
  const { setLoading } = useContext(AbTestsAddEditContext)
  const [errors, setErrors] = useState<FieldErrors<AbTestsAddSchema>>({})

  const { isFeatureEnabled: withYp2395 } = useFeatureFlag('yp2395AbTestsDivideTraffic')

  const { isPending: loading, mutateAsync } = useMutation<{ input: Variables }, Data>({
    mutation: UPDATE_AB_TEST,
    onCompleted: async (data: Data) => {
      const {
        updateAbTest: { abTest, errors },
      } = data

      if (errors && errors.length > 0) {
        setErrors(
          mapGraphQLErrors(errors, (attribute: MutationInputError['attribute']) =>
            fixAttributeDuplicatePaths(attribute),
          ),
        )
        enqueueSnackbar(t('error'), { variant: 'error' })
      } else if (abTest) {
        setErrors({})
        enqueueSnackbar(t('success'), { variant: 'success' })

        if (abTest.status === 'active') {
          navigate(generatePath(ROUTES.AB_TESTS.LIST, { workspaceId }))
        }
      }
    },
  })

  useEffect(() => {
    setLoading(loading)
  }, [loading])

  const update: UseUpdate['update'] = ({
    cookieLifetime,
    id,
    name,
    status,
    trafficSplitType,
    variants,
  }): void =>
    void mutateAsync({
      input: toDTO({
        cookieLifetime,
        id,
        name,
        status,
        trafficSplitType,
        variants,
        withYp2395,
      }),
    })

  return { errors, update }
}
